import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDashboardStore } from "@/stores/useDashboardStore";

import useAxiosPrivate from "../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../api/resources";
import useResponse from "../../../../../shared/hooks/useResponse";

type DownloadResource =
  | ApiResources.DashboardDriverDownloads
  | ApiResources.DashboardVehicleDownloads;

const useResourceDownloadsStatus = (resource: DownloadResource) => {
  const { handleCommonError } = useResponse();
  const { getData } = useAxiosPrivate();
  const { t } = useTranslation();

  const {
    setDriverDownloadsStatus,
    setDriverDownloadsError,
    setVehicleDownloadsStatus,
    setVehicleDownloadsError
  } = useDashboardStore();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const initialized = useRef(false);

  const setters = {
    [ApiResources.DashboardDriverDownloads]: {
      setStatus: setDriverDownloadsStatus,
      setError: setDriverDownloadsError
    },
    [ApiResources.DashboardVehicleDownloads]: {
      setStatus: setVehicleDownloadsStatus,
      setError: setVehicleDownloadsError
    }
  };

  const getDownloadStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getData(resource);
      setters[resource].setStatus(response.data);
    } catch (error) {
      handleCommonError(error, t);
      setters[resource].setError(true);
    } finally {
      initialized.current = true;
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData, handleCommonError, resource]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      getDownloadStatus();
    }
  }, [getDownloadStatus]);

  return { isLoading };
};

export default useResourceDownloadsStatus;
